import { Link } from "@tanstack/react-router";
import { Loader2, type LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/** Loading indicator that screen readers announce (`role="status"`). */
export function LoadingState({
  label,
  className,
  announce = true,
}: {
  label?: string;
  className?: string;
  announce?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div
      role={announce ? "status" : undefined}
      className={cn(
        "text-muted-foreground flex items-center justify-center gap-2 py-10 text-sm",
        className,
      )}
    >
      <Loader2 className="size-4 animate-spin" aria-hidden />
      <span>{label ?? t("states.loading")}</span>
    </div>
  );
}

/** Retry the current query without replacing cached content or resetting the view. */
export function QueryError({
  message,
  cached = false,
  retrying,
  onRetry,
  announce = true,
}: {
  message: string;
  cached?: boolean;
  retrying: boolean;
  onRetry: () => void;
  announce?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="grid justify-items-start gap-2 rounded-md border p-4">
      <p
        role={announce ? (retrying ? "status" : "alert") : undefined}
        className="text-destructive text-sm"
      >
        {retrying ? t("states.retrying") : message}
        {cached && !retrying ? ` ${t("states.cached")}` : null}
      </p>
      <Button variant="outline" size="sm" disabled={retrying} onClick={onRetry}>
        {retrying ? t("states.retrying") : t("states.retry")}
      </Button>
    </div>
  );
}

/** Shown in place of a list without entries: short title, optional explanation and one action. */
export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  className,
  testId,
}: {
  icon?: LucideIcon;
  title: string;
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
  testId?: string;
}) {
  return (
    <div
      data-testid={testId}
      className={cn(
        "grid justify-items-center gap-2 rounded-lg border border-dashed px-6 py-10 text-center",
        className,
      )}
    >
      {Icon ? <Icon className="text-muted-foreground size-8" aria-hidden /> : null}
      <p className="font-medium">{title}</p>
      {description ? <p className="text-muted-foreground text-sm">{description}</p> : null}
      {action ? <div className="mt-2">{action}</div> : null}
    </div>
  );
}

/** Full-page message (not found, no permission, error) with a title, an explanation and an action. */
export function StatusPage({
  icon: Icon,
  title,
  description,
  action,
}: {
  icon?: LucideIcon;
  title: string;
  description: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="mx-auto grid max-w-md justify-items-center gap-4 px-4 py-16 text-center">
      {Icon ? <Icon className="text-muted-foreground size-10" aria-hidden /> : null}
      <h1 tabIndex={-1} className="text-2xl font-semibold">
        {title}
      </h1>
      <p className="text-muted-foreground">{description}</p>
      {action}
    </div>
  );
}

/** The "back to the library" action used by not-found and no-permission pages. */
export function HomeLink() {
  const { t } = useTranslation();
  return (
    <Button asChild variant="outline">
      <Link to="/">{t("notFound.home")}</Link>
    </Button>
  );
}
